import { MappedField } from 'libs/datagrid/src/lib/containers/dx-datagrid/dx-datagrid.types';
import { hasAllowedFields } from './field-structure.utils';

export type StocksFields = {
  /**
   * Required for form
   */
  controlType?: StocksPlaceHolderTypeVal;
  /**
   * Shows dataType tah
   */
  dataType?: StocksPlaceHolderTypeVal;
  editType?: string;
  /**
   * Shows if field should be excluded in specific forms
   */
  exclude?: StocksPlaceHolderTypeVal;
  exclusive?: string;
  format?: string;
  /**
   * Shows the name that should be displayed in forms
   */
  headerText?: StocksPlaceHolderTypeVal;

  /**
   * Used as field in forms
   */
  id: StocksPlaceHolderTypeVal;
  /**
   * (Datagrid)
   */
  isPrimaryKey?: boolean;
  /**
   * Shows if the field is mandatory in forms
   */
  isRequired?: StocksPlaceHolderTypeVal;
  /**
   * Shows if it should be visible by default in forms
   */
  visible?: StocksPlaceHolderTypeVal;
};

export type StocksPlaceHolderTypeVal = {
  createForm?: boolean | number | string;
  datagrid?: boolean | number | string;
  editForm?: boolean | number | string;
};

export enum EnumStocksViews {
  EDIT = 0,
  CONTACT = 1,
  NOTIFICATIONS = 2,
}

export enum EnumStocksFieldStructure {
  StocksTable = 0,
  StocksCreateForm = 1,
  StocksEditForm = 2,
}

export class StocksListField {
  fields: MappedField[];
  userRightFields: string[] = [];
  hasField: (
    field: StocksFields,
    refId: keyof StocksPlaceHolderTypeVal,
    actualId: keyof StocksPlaceHolderTypeVal,
  ) => boolean;

  constructor(type?: EnumStocksFieldStructure, userRightFields?: string[]) {
    if (hasAllowedFields(userRightFields)) {
      this.userRightFields = userRightFields.map((e): string => e.toLowerCase());
      this.hasField = this.validFunWithUserFields;
    } else {
      this.hasField = this.validFunWithoutUserFields;
    }
    this.fields = this.getDefaultFields(type);
  }

  getDefaultFields(type?: EnumStocksFieldStructure): MappedField[] {
    switch (type) {
      case EnumStocksFieldStructure.StocksTable:
        return this.getDatagridFields();
      case EnumStocksFieldStructure.StocksCreateForm:
        return this.getCreateFormList();
      case EnumStocksFieldStructure.StocksEditForm:
        return this.getEditFormList();
      default:
        return this.getDatagridFields();
    }
  }

  /**
   *
   * @param field field which inclusiveness needs to check
   * @param refId Id
   * @param actualId actual Id on which fields belongs
   * @returns is valid field
   */
  validFunWithUserFields = (
    field: StocksFields,
    refId: keyof StocksPlaceHolderTypeVal,
    actualId: keyof StocksPlaceHolderTypeVal,
  ): boolean => {
    if (this.userRightFields && !(field.isRequired && field.isRequired[actualId])) {
      //For adding fields not present in default fields
      return (
        this.userRightFields.includes((<string>field.id[refId]).toLowerCase()) &&
        !(field.exclude && field.exclude[actualId] == true)
      );
    } else {
      return this.validFunWithoutUserFields(field, refId, actualId);
    }
  };

  validFunWithoutUserFields = (
    field: StocksFields,
    refId: keyof StocksPlaceHolderTypeVal,
    actualId: keyof StocksPlaceHolderTypeVal,
  ): boolean => {
    if (field.exclude && field.exclude[actualId] == true) {
      return false;
    } else {
      return true;
    }
  };

  getDatagridFields(): MappedField[] {
    return this.StocksList.filter((field: StocksFields): boolean => {
      return this.hasField(field, 'datagrid', 'datagrid');
    }).map((field: StocksFields): MappedField => {
      return {
        field: (field.id?.datagrid as string) ?? '',
        headerText: (field.headerText?.datagrid as string) ?? '',
        isRequired: !!field.isRequired?.datagrid,
        type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
        visible: !!field.visible?.datagrid,
      };
    });
  }

  getCreateFormList(): MappedField[] {
    return this.StocksList.filter((field: StocksFields): boolean => {
      return this.hasField(field, 'datagrid', 'createForm');
    }).map((field: StocksFields): MappedField => {
      return {
        controlType: field.controlType?.createForm ? (field.controlType.createForm as string) : '',
        field: (field.id?.datagrid as string) ?? '',
        headerText:
          (field.headerText?.createForm as string) ?? (field.headerText?.datagrid as string) ?? '',
        isRequired: !!field.isRequired?.createForm,
        visible: !!field.visible?.datagrid,
      };
    });
  }

  getEditFormList(): MappedField[] {
    return this.StocksList.filter((field: StocksFields): boolean => {
      return this.hasField(field, 'datagrid', 'editForm');
    }).map((field: StocksFields): MappedField => {
      return {
        controlType: field.controlType?.editForm ? (field.controlType.editForm as string) : '',
        field: (field.id?.datagrid as string) ?? '',
        headerText:
          (field.headerText?.editForm as string) ?? (field.headerText?.datagrid as string) ?? '',
        isRequired: !!field.isRequired?.editForm,
        visible: !!field.visible?.datagrid,
      };
    });
  }

  public readonly StocksList: StocksFields[] = [
    {
      dataType: { datagrid: 'actions' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      isRequired: { datagrid: true },
      visible: { datagrid: true },
    },
    {
      controlType: { editForm: 'simple' },
      dataType: { datagrid: 'string' },
      exclude: { createForm: true, datagrid: true },
      headerText: { datagrid: 'ID' },
      id: { datagrid: 'id' },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'simple', editForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Storage Location' },
      id: { datagrid: 'name' },
      isRequired: { createForm: true },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'simple', editForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Building' },
      id: { datagrid: 'building' },
      isRequired: { createForm: true },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'simple', editForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Information' },
      id: { datagrid: 'information' },
      isRequired: { createForm: false },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'simple', editForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Company' },
      id: { datagrid: 'company' },
      isRequired: { createForm: false },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Contact' },
      id: { datagrid: 'contact' },
      visible: { datagrid: false },
    },
  ];
}
